import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { LaunchTrajectoryChart, LaunchFunnelKPIs } from '@/components/launch';
import { OpportunityCard } from '@/components/ui';

interface LaunchAssumptions {
  productName: string;
  launchPrice: number;
  trialRate: number;
  repeatRate: number;
  distributionMonth3: number;
  distributionMonth6: number;
  distributionMonth12: number;
  marketingSpend: 'low' | 'medium' | 'high';
}

// Confidence interval results
interface ConfidenceInterval {
  p10: number;  // Pessimistic (10th percentile)
  p50: number;  // Expected (median)
  p90: number;  // Optimistic (90th percentile)
}

interface MonthlyForecastWithCI {
  month: number;
  volume: ConfidenceInterval;
  revenue: ConfidenceInterval;
  distribution: number;
}

interface ForecastResult {
  verdict: 'go' | 'refine' | 'pause';
  confidence: number;
  year1Volume: number;
  year1Revenue: number;
  marketShare: number;
  // Confidence intervals
  volumeCI: ConfidenceInterval;
  revenueCI: ConfidenceInterval;
  marketShareCI: ConfidenceInterval;
  monthlyForecastCI: MonthlyForecastWithCI[];
  // Legacy single-point forecast for backward compatibility
  monthlyForecast: Array<{ month: number; volume: number; revenue: number; distribution: number }>;
  sourceOfVolume: {
    marketGrowth: number;
    competitorSwitch: number;
    newUsers: number;
    ownCannibalization: number;
  };
  cannibalizationRisk: 'low' | 'medium' | 'high';
  keyRisks: string[];
  keyOpportunities: string[];
  // Monte Carlo stats
  simulationRuns: number;
  probabilityOfSuccess: number; // % of simulations meeting success threshold
  // Launch trajectory funnel metrics
  trajectoryData: Array<{
    period: string;
    volume: number;
    awareness: number;
    trial: number;
    repeat: number;
  }>;
  finalMetrics: {
    awarenessTarget: number;
    awarenessFinal: number;
    trialTarget: number;
    trialFinal: number;
    repeatTarget: number;
    repeatFinal: number;
  };
}

// =============================================================================
// MONTE CARLO SIMULATION
// =============================================================================

// Simple seeded random for reproducibility (Mulberry32)
function mulberry32(seed: number) {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// Box-Muller transform for normal distribution
function normalRandom(mean: number, stdDev: number, rand: () => number): number {
  const u1 = rand();
  const u2 = rand();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z * stdDev;
}

// Calculate percentile from sorted array
function percentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const index = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

interface SimulationInput {
  trialRate: number;
  repeatRate: number;
  distributionMonth3: number;
  distributionMonth6: number;
  distributionMonth12: number;
  launchPrice: number;
  marketVolume: number;
}

interface SimulationOutput {
  year1Volume: number;
  year1Revenue: number;
  marketShare: number;
  monthlyVolumes: number[];
  monthlyRevenues: number[];
}

function runSingleSimulation(
  input: SimulationInput,
  rand: () => number
): SimulationOutput {
  // Add uncertainty to inputs (coefficient of variation ~15-25%)
  const trialRate = Math.max(0.01, normalRandom(input.trialRate, input.trialRate * 0.20, rand));
  const repeatRate = Math.max(0.1, Math.min(0.8, normalRandom(input.repeatRate, input.repeatRate * 0.15, rand)));
  const distMonth3 = Math.max(0.05, Math.min(0.6, normalRandom(input.distributionMonth3, input.distributionMonth3 * 0.25, rand)));
  const distMonth6 = Math.max(distMonth3, Math.min(0.8, normalRandom(input.distributionMonth6, input.distributionMonth6 * 0.20, rand)));
  const distMonth12 = Math.max(distMonth6, Math.min(0.95, normalRandom(input.distributionMonth12, input.distributionMonth12 * 0.15, rand)));

  const monthlyVolumes: number[] = [];
  const monthlyRevenues: number[] = [];
  let cumulativeVolume = 0;

  for (let month = 1; month <= 12; month++) {
    // Distribution ramp with uncertainty
    let distribution: number;
    if (month <= 3) {
      distribution = distMonth3 * (month / 3);
    } else if (month <= 6) {
      distribution = distMonth3 + (distMonth6 - distMonth3) * ((month - 3) / 3);
    } else {
      distribution = distMonth6 + (distMonth12 - distMonth6) * ((month - 6) / 6);
    }

    // Trial + repeat model with some randomness
    const monthlyMarketVolume = input.marketVolume / 12;
    const trialMultiplier = month === 1 ? 1 : normalRandom(0.3, 0.05, rand);
    const trialVolume = monthlyMarketVolume * trialRate * distribution * Math.max(0.1, trialMultiplier);
    const repeatVolume = cumulativeVolume * repeatRate * normalRandom(0.15, 0.02, rand);
    const volume = Math.max(0, Math.round(trialVolume + repeatVolume));
    const revenue = volume * input.launchPrice;

    monthlyVolumes.push(volume);
    monthlyRevenues.push(revenue);
    cumulativeVolume += volume;
  }

  const year1Volume = monthlyVolumes.reduce((sum, v) => sum + v, 0);
  const year1Revenue = monthlyRevenues.reduce((sum, r) => sum + r, 0);
  const marketShare = (year1Volume / input.marketVolume) * 100;

  return { year1Volume, year1Revenue, marketShare, monthlyVolumes, monthlyRevenues };
}

function runMonteCarloSimulation(
  input: SimulationInput,
  runs: number = 1000
): {
  volumeCI: ConfidenceInterval;
  revenueCI: ConfidenceInterval;
  marketShareCI: ConfidenceInterval;
  monthlyCI: MonthlyForecastWithCI[];
  probabilityOfSuccess: number;
  medianResult: SimulationOutput;
} {
  const rand = mulberry32(42); // Fixed seed for reproducibility
  const results: SimulationOutput[] = [];

  for (let i = 0; i < runs; i++) {
    results.push(runSingleSimulation(input, rand));
  }

  // Extract arrays for percentile calculation
  const volumes = results.map(r => r.year1Volume);
  const revenues = results.map(r => r.year1Revenue);
  const marketShares = results.map(r => r.marketShare);

  // Calculate monthly CIs
  const monthlyCI: MonthlyForecastWithCI[] = [];
  for (let month = 0; month < 12; month++) {
    const monthVolumes = results.map(r => r.monthlyVolumes[month]);
    const monthRevenues = results.map(r => r.monthlyRevenues[month]);

    // Distribution is deterministic (from input)
    let distribution: number;
    if (month + 1 <= 3) {
      distribution = input.distributionMonth3 * ((month + 1) / 3);
    } else if (month + 1 <= 6) {
      distribution = input.distributionMonth3 + (input.distributionMonth6 - input.distributionMonth3) * ((month - 2) / 3);
    } else {
      distribution = input.distributionMonth6 + (input.distributionMonth12 - input.distributionMonth6) * ((month - 5) / 6);
    }

    monthlyCI.push({
      month: month + 1,
      volume: {
        p10: percentile(monthVolumes, 10),
        p50: percentile(monthVolumes, 50),
        p90: percentile(monthVolumes, 90),
      },
      revenue: {
        p10: percentile(monthRevenues, 10),
        p50: percentile(monthRevenues, 50),
        p90: percentile(monthRevenues, 90),
      },
      distribution,
    });
  }

  // Probability of success (market share > 1%)
  const successThreshold = 1.0;
  const successCount = marketShares.filter(ms => ms >= successThreshold).length;
  const probabilityOfSuccess = successCount / runs;

  // Find median result for single-point estimate
  const medianVolume = percentile(volumes, 50);
  const medianIdx = volumes.findIndex(v => v >= medianVolume);
  const medianResult = results[medianIdx] || results[Math.floor(runs / 2)];

  return {
    volumeCI: {
      p10: percentile(volumes, 10),
      p50: percentile(volumes, 50),
      p90: percentile(volumes, 90),
    },
    revenueCI: {
      p10: percentile(revenues, 10),
      p50: percentile(revenues, 50),
      p90: percentile(revenues, 90),
    },
    marketShareCI: {
      p10: percentile(marketShares, 10),
      p50: percentile(marketShares, 50),
      p90: percentile(marketShares, 90),
    },
    monthlyCI,
    probabilityOfSuccess,
    medianResult,
  };
}

export function LaunchPage() {
  const { clientId } = useParams();
  const [step, setStep] = useState<'configure' | 'assumptions' | 'results'>('configure');
  const [assumptions, setAssumptions] = useState<LaunchAssumptions>({
    productName: 'Polet Premium Mango',
    launchPrice: 2500,
    trialRate: 0.15,
    repeatRate: 0.40,
    distributionMonth3: 0.30,
    distributionMonth6: 0.50,
    distributionMonth12: 0.70,
    marketingSpend: 'medium',
  });

  // Simulate forecast based on assumptions with Monte Carlo
  const forecast = useMemo<ForecastResult | null>(() => {
    if (step !== 'results') return null;

    // Base market size (from Meals de Colombia ice cream market)
    const marketVolume = 850000000; // 850M units annually
    const SIMULATION_RUNS = 1000;

    // Run Monte Carlo simulation
    const simulationInput: SimulationInput = {
      trialRate: assumptions.trialRate,
      repeatRate: assumptions.repeatRate,
      distributionMonth3: assumptions.distributionMonth3,
      distributionMonth6: assumptions.distributionMonth6,
      distributionMonth12: assumptions.distributionMonth12,
      launchPrice: assumptions.launchPrice,
      marketVolume,
    };

    const mcResults = runMonteCarloSimulation(simulationInput, SIMULATION_RUNS);

    // Use p50 (median) as the primary estimate
    const year1Volume = mcResults.volumeCI.p50;
    const year1Revenue = mcResults.revenueCI.p50;
    const marketShare = mcResults.marketShareCI.p50;

    // Legacy monthly forecast (using median values)
    const monthlyForecast = mcResults.monthlyCI.map(m => ({
      month: m.month,
      volume: m.volume.p50,
      revenue: m.revenue.p50,
      distribution: m.distribution,
    }));

    // Source of volume (based on typical CPG launch patterns)
    const sourceOfVolume = {
      marketGrowth: 0.15,
      competitorSwitch: 0.45,
      newUsers: 0.25,
      ownCannibalization: 0.15,
    };

    // Determine verdict based on probability of success and p10 floor
    let verdict: 'go' | 'refine' | 'pause';
    let confidence: number;

    // Use probability of success and pessimistic scenario for verdict
    const p10MarketShare = mcResults.marketShareCI.p10;
    const probSuccess = mcResults.probabilityOfSuccess;

    if (probSuccess > 0.75 && p10MarketShare > 0.8 && sourceOfVolume.ownCannibalization < 0.2) {
      // High probability of success even in pessimistic scenario
      verdict = 'go';
      confidence = probSuccess;
    } else if (probSuccess > 0.50 && marketShare > 0.8) {
      // Decent chance but needs refinement
      verdict = 'refine';
      confidence = probSuccess;
    } else {
      // Too risky
      verdict = 'pause';
      confidence = probSuccess;
    }

    // Generate trajectory data with funnel metrics
    const trajectoryData = monthlyForecast.map((m, idx) => {
      // Awareness builds from marketing spend + distribution
      const marketingMultiplier = assumptions.marketingSpend === 'high' ? 1.3 :
                                   assumptions.marketingSpend === 'medium' ? 1.0 : 0.7;
      const maxAwareness = 50 + marketingMultiplier * 30; // 50-80% max awareness
      const awareness = Math.min(maxAwareness, 15 + (idx * 6 * marketingMultiplier) + (m.distribution * 40));

      // Trial is % of aware consumers who try
      const trialConversion = assumptions.trialRate * (1 + idx * 0.02); // Improves slightly over time
      const trial = Math.min(awareness * trialConversion, awareness * 0.5);

      // Repeat is % of triers who repeat
      const repeatConversion = assumptions.repeatRate * (1 + idx * 0.015);
      const repeat = idx < 2 ? 0 : Math.min(trial * repeatConversion, trial * 0.8);

      return {
        period: `M${m.month}`,
        volume: m.volume,
        awareness: Math.round(awareness * 10) / 10,
        trial: Math.round(trial * 10) / 10,
        repeat: Math.round(repeat * 10) / 10,
      };
    });

    // Final metrics (Month 12)
    const finalMetrics = {
      awarenessTarget: 70,
      awarenessFinal: trajectoryData[11]?.awareness || 0,
      trialTarget: assumptions.trialRate * 100,
      trialFinal: trajectoryData[11]?.trial || 0,
      repeatTarget: assumptions.repeatRate * 100,
      repeatFinal: trajectoryData[11]?.repeat || 0,
    };

    return {
      verdict,
      confidence,
      year1Volume,
      year1Revenue,
      marketShare,
      // Confidence intervals
      volumeCI: mcResults.volumeCI,
      revenueCI: mcResults.revenueCI,
      marketShareCI: mcResults.marketShareCI,
      monthlyForecastCI: mcResults.monthlyCI,
      // Legacy
      monthlyForecast,
      sourceOfVolume,
      cannibalizationRisk: sourceOfVolume.ownCannibalization > 0.25 ? 'high' :
                          sourceOfVolume.ownCannibalization > 0.15 ? 'medium' : 'low',
      keyRisks: [
        `Pessimistic scenario (p10): ${mcResults.marketShareCI.p10.toFixed(1)}% share`,
        'Moderate cannibalization from existing Polet line',
        'Competitive response expected from Crem Helado',
        'Distribution build may be slower in traditional trade',
      ],
      keyOpportunities: [
        `Optimistic scenario (p90): ${mcResults.marketShareCI.p90.toFixed(1)}% share`,
        'Premium positioning captures underserved segment',
        'Mango flavor fills gap in current portfolio',
        'Strong brand equity from Polet can be leveraged',
      ],
      simulationRuns: SIMULATION_RUNS,
      probabilityOfSuccess: probSuccess,
      trajectoryData,
      finalMetrics,
    };
  }, [step, assumptions]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-white mb-2">Launch Planning</h1>
      <p className="text-text-secondary mb-6">Forecast volume, assess cannibalization, get a verdict</p>

      {/* Step indicator */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { key: 'configure', label: 'Product' },
          { key: 'assumptions', label: 'Assumptions' },
          { key: 'results', label: 'Forecast' },
        ].map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step === s.key ? 'bg-primary-600 text-white' :
              ['configure', 'assumptions', 'results'].indexOf(step) > i ? 'bg-success-600 text-white' :
              'bg-surface-600 text-text-secondary'
            }`}>
              {['configure', 'assumptions', 'results'].indexOf(step) > i ? '✓' : i + 1}
            </div>
            <span className={`text-sm ${step === s.key ? 'text-white' : 'text-text-secondary'}`}>
              {s.label}
            </span>
            {i < 2 && <div className="w-12 h-px bg-surface-400/30" />}
          </div>
        ))}
      </div>

      {/* Step 1: Configure Product */}
      {step === 'configure' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">New Product</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Product Name</label>
                <input
                  type="text"
                  value={assumptions.productName}
                  onChange={(e) => setAssumptions({ ...assumptions, productName: e.target.value })}
                  className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Category</label>
                <select className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white">
                  <option>Paletas Premium</option>
                  <option>Paletas Agua</option>
                  <option>Conos</option>
                  <option>Take-Home</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Launch Price (COP)</label>
                <input
                  type="number"
                  value={assumptions.launchPrice}
                  onChange={(e) => setAssumptions({ ...assumptions, launchPrice: parseInt(e.target.value) || 0 })}
                  className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Market Context</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Target Market</label>
                <select className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white">
                  <option>Colombia - National</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Distribution Channel</label>
                <select className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white">
                  <option>All Channels</option>
                  <option>Tiendas + Supermercados</option>
                  <option>Modern Trade Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Launch Quarter</label>
                <select className="w-full bg-surface-600 border border-surface-400/30 rounded-lg px-3 py-2 text-white">
                  <option>Q2 2026 (Peak Season)</option>
                  <option>Q1 2026</option>
                  <option>Q3 2026</option>
                  <option>Q4 2026</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Assumptions */}
      {step === 'assumptions' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Consumer Assumptions</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Trial Rate</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="5" max="30"
                    value={assumptions.trialRate * 100}
                    onChange={(e) => setAssumptions({ ...assumptions, trialRate: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-12">{(assumptions.trialRate * 100).toFixed(0)}%</span>
                </div>
                <p className="text-xs text-text-tertiary mt-1">% of target consumers who will try the product</p>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Repeat Rate</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="20" max="60"
                    value={assumptions.repeatRate * 100}
                    onChange={(e) => setAssumptions({ ...assumptions, repeatRate: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-12">{(assumptions.repeatRate * 100).toFixed(0)}%</span>
                </div>
                <p className="text-xs text-text-tertiary mt-1">% of triers who become repeat buyers</p>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Marketing Investment</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['low', 'medium', 'high'] as const).map((level) => (
                    <button
                      key={level}
                      onClick={() => setAssumptions({ ...assumptions, marketingSpend: level })}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        assumptions.marketingSpend === level
                          ? 'bg-primary-600 text-white'
                          : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Distribution Build</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Month 3 Distribution</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="10" max="50"
                    value={assumptions.distributionMonth3 * 100}
                    onChange={(e) => setAssumptions({ ...assumptions, distributionMonth3: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-12">{(assumptions.distributionMonth3 * 100).toFixed(0)}%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Month 6 Distribution</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="30" max="70"
                    value={assumptions.distributionMonth6 * 100}
                    onChange={(e) => setAssumptions({ ...assumptions, distributionMonth6: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-12">{(assumptions.distributionMonth6 * 100).toFixed(0)}%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Month 12 Distribution</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="50" max="90"
                    value={assumptions.distributionMonth12 * 100}
                    onChange={(e) => setAssumptions({ ...assumptions, distributionMonth12: parseInt(e.target.value) / 100 })}
                    className="flex-1"
                  />
                  <span className="text-white font-medium w-12">{(assumptions.distributionMonth12 * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>

            {/* Distribution chart preview */}
            <div className="mt-6 h-24 flex items-end gap-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
                let dist;
                if (month <= 3) {
                  dist = assumptions.distributionMonth3 * (month / 3);
                } else if (month <= 6) {
                  dist = assumptions.distributionMonth3 +
                    (assumptions.distributionMonth6 - assumptions.distributionMonth3) * ((month - 3) / 3);
                } else {
                  dist = assumptions.distributionMonth6 +
                    (assumptions.distributionMonth12 - assumptions.distributionMonth6) * ((month - 6) / 6);
                }
                return (
                  <div key={month} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-primary-500 rounded-t"
                      style={{ height: `${dist * 100}%` }}
                    />
                    <span className="text-xs text-text-tertiary mt-1">{month}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 'results' && forecast && (
        <div className="space-y-6">
          {/* Verdict Banner */}
          <div className={`card p-6 border-l-4 ${
            forecast.verdict === 'go' ? 'border-l-success-500 bg-success-500/5' :
            forecast.verdict === 'refine' ? 'border-l-warning-500 bg-warning-500/5' :
            'border-l-danger-500 bg-danger-500/5'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <span className={`text-3xl font-bold ${
                    forecast.verdict === 'go' ? 'text-success-400' :
                    forecast.verdict === 'refine' ? 'text-warning-400' :
                    'text-danger-400'
                  }`}>
                    {forecast.verdict.toUpperCase()}
                  </span>
                  <span className="text-text-secondary">|</span>
                  <span className="text-text-secondary">
                    {(forecast.probabilityOfSuccess * 100).toFixed(0)}% probability of success
                  </span>
                </div>
                <p className="text-text-secondary mt-1">
                  {forecast.verdict === 'go'
                    ? 'Strong launch potential with manageable cannibalization'
                    : forecast.verdict === 'refine'
                    ? 'Adjust pricing or distribution strategy before launch'
                    : 'Reconsider product positioning or timing'}
                </p>
                <p className="text-xs text-text-tertiary mt-2">
                  Based on {forecast.simulationRuns.toLocaleString()} Monte Carlo simulations
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  forecast.verdict === 'go' ? 'bg-success-500/20' :
                  forecast.verdict === 'refine' ? 'bg-warning-500/20' :
                  'bg-danger-500/20'
                }`}>
                  <span className="text-4xl">
                    {forecast.verdict === 'go' ? '✓' : forecast.verdict === 'refine' ? '!' : '×'}
                  </span>
                </div>
                {/* Probability meter */}
                <div className="w-20 h-2 bg-surface-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      forecast.probabilityOfSuccess > 0.7 ? 'bg-success-500' :
                      forecast.probabilityOfSuccess > 0.4 ? 'bg-warning-500' :
                      'bg-danger-500'
                    }`}
                    style={{ width: `${forecast.probabilityOfSuccess * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics with Confidence Intervals */}
          <div className="grid grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Year 1 Volume</div>
              <div className="text-2xl font-bold text-white">
                {(forecast.volumeCI.p50 / 1000000).toFixed(1)}M
              </div>
              <div className="text-xs text-text-tertiary">units (p50)</div>
              <div className="mt-2 text-xs">
                <span className="text-danger-400">{(forecast.volumeCI.p10 / 1000000).toFixed(1)}M</span>
                <span className="text-surface-400 mx-1">→</span>
                <span className="text-success-400">{(forecast.volumeCI.p90 / 1000000).toFixed(1)}M</span>
              </div>
              <div className="text-[10px] text-surface-400">p10 → p90 range</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Year 1 Revenue</div>
              <div className="text-2xl font-bold text-primary-400">
                ${(forecast.revenueCI.p50 / 1000000000).toFixed(1)}B
              </div>
              <div className="text-xs text-text-tertiary">COP (p50)</div>
              <div className="mt-2 text-xs">
                <span className="text-danger-400">${(forecast.revenueCI.p10 / 1000000000).toFixed(1)}B</span>
                <span className="text-surface-400 mx-1">→</span>
                <span className="text-success-400">${(forecast.revenueCI.p90 / 1000000000).toFixed(1)}B</span>
              </div>
              <div className="text-[10px] text-surface-400">p10 → p90 range</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Market Share</div>
              <div className="text-2xl font-bold text-white">
                {forecast.marketShareCI.p50.toFixed(1)}%
              </div>
              <div className="text-xs text-text-tertiary">of category (p50)</div>
              <div className="mt-2 text-xs">
                <span className="text-danger-400">{forecast.marketShareCI.p10.toFixed(1)}%</span>
                <span className="text-surface-400 mx-1">→</span>
                <span className="text-success-400">{forecast.marketShareCI.p90.toFixed(1)}%</span>
              </div>
              <div className="text-[10px] text-surface-400">p10 → p90 range</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Cannibalization</div>
              <div className={`text-2xl font-bold ${
                forecast.cannibalizationRisk === 'low' ? 'text-success-400' :
                forecast.cannibalizationRisk === 'medium' ? 'text-warning-400' :
                'text-danger-400'
              }`}>
                {forecast.cannibalizationRisk.toUpperCase()}
              </div>
              <div className="text-xs text-text-tertiary">risk level</div>
              <div className="mt-2 text-xs text-text-tertiary">
                {(forecast.sourceOfVolume.ownCannibalization * 100).toFixed(0)}% of volume
              </div>
            </div>
          </div>

          {/* Confidence Interval Explanation */}
          <div className="bg-surface-700/30 border border-surface-600/30 rounded-lg p-4 flex items-center gap-4">
            <span className="text-xl">📊</span>
            <div className="text-sm">
              <span className="text-text-secondary">Confidence intervals show the range of likely outcomes: </span>
              <span className="text-danger-400">p10</span>
              <span className="text-text-tertiary"> (pessimistic, 10% chance of being lower) → </span>
              <span className="text-white">p50</span>
              <span className="text-text-tertiary"> (expected median) → </span>
              <span className="text-success-400">p90</span>
              <span className="text-text-tertiary"> (optimistic, 10% chance of being higher)</span>
            </div>
          </div>

          {/* Launch Trajectory Chart */}
          <div className="card p-6">
            <LaunchTrajectoryChart
              data={forecast.trajectoryData}
              title="Launch Trajectory"
              height={300}
              showLegend={true}
            />
          </div>

          {/* Funnel KPIs */}
          <LaunchFunnelKPIs
            awarenessTarget={forecast.finalMetrics.awarenessTarget}
            awarenessFinal={forecast.finalMetrics.awarenessFinal}
            trialTarget={forecast.finalMetrics.trialTarget}
            trialFinal={forecast.finalMetrics.trialFinal}
            repeatTarget={forecast.finalMetrics.repeatTarget}
            repeatFinal={forecast.finalMetrics.repeatFinal}
            volumeFinal={forecast.monthlyForecast[11]?.volume || 0}
          />

          {/* Source of Volume */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Source of Volume</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Competitor Switch', value: forecast.sourceOfVolume.competitorSwitch, color: 'bg-success-500' },
                { label: 'New Category Users', value: forecast.sourceOfVolume.newUsers, color: 'bg-primary-500' },
                { label: 'Market Growth', value: forecast.sourceOfVolume.marketGrowth, color: 'bg-blue-500' },
                { label: 'Own Cannibalization', value: forecast.sourceOfVolume.ownCannibalization, color: 'bg-danger-500' },
              ].map((source) => (
                <div key={source.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-text-secondary">{source.label}</span>
                    <span className="text-white font-medium">{(source.value * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${source.color} rounded-full transition-all`}
                      style={{ width: `${source.value * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Evaluation Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <OpportunityCard
              icon="✓"
              title="Launch Evaluation"
              status={forecast.verdict === 'go' ? 'approved' : forecast.verdict === 'refine' ? 'review' : 'risk'}
              confidence={Math.round(forecast.probabilityOfSuccess * 100)}
              reasons={forecast.keyOpportunities.slice(0, 3)}
              recommendation={
                forecast.verdict === 'go'
                  ? 'Proceed with launch plan. Consider geographic expansion in Phase 2.'
                  : forecast.verdict === 'refine'
                  ? 'Adjust pricing or distribution strategy before launch.'
                  : 'Reconsider product positioning or timing before proceeding.'
              }
              potential={`${(forecast.volumeCI.p50 / 1000000).toFixed(1)}M units Year 1`}
            />
            <OpportunityCard
              icon="⚠"
              title="Identified Risks"
              status="review"
              confidence={100 - Math.round(forecast.probabilityOfSuccess * 100)}
              reasons={forecast.keyRisks.slice(0, 3)}
              recommendation={
                forecast.cannibalizationRisk === 'high'
                  ? 'Address cannibalization risk with differentiated positioning.'
                  : 'Monitor competitive response and distribution build weekly for first 8 weeks.'
              }
              potential={`${forecast.cannibalizationRisk.toUpperCase()} cannibalization risk`}
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="mt-6 flex justify-between">
        {step !== 'configure' && (
          <button
            onClick={() => setStep(step === 'assumptions' ? 'configure' : 'assumptions')}
            className="btn bg-surface-600 text-white hover:bg-surface-500"
          >
            Back
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={() => setStep(
            step === 'configure' ? 'assumptions' :
            step === 'assumptions' ? 'results' :
            'configure'
          )}
          className="btn btn-primary"
        >
          {step === 'configure' ? 'Next: Set Assumptions' :
           step === 'assumptions' ? 'Run Forecast' :
           'New Simulation'}
        </button>
      </div>
    </div>
  );
}
